<template>
  <!-- Phone toolbar shared by the four booking views (OTC-166). Below 992px: phones in either
       orientation and tablets in portrait (OTC-2955); hidden from 992px.
       Row 1: view switcher and actions, gear at the far right.
       Row 2: the date control at full width (arrows, compact date label, Today).
       Row 3: the range as one full-width select plus a single filter button that shows or hides
       the host view's filter row (the host keeps that row mounted so saved views still apply). -->
  <div class="booking-mobile-toolbar" :class="{ 'booking-mobile-toolbar--always': alwaysShow }">
    <div class="booking-mobile-toolbar__top">
      <slot name="switcher"></slot>
      <slot name="actions"></slot>
      <div class="booking-mobile-toolbar__gear"><slot name="gear"></slot></div>
    </div>

    <div v-if="range !== 'rolling-30'" class="booking-mobile-toolbar__nav">
      <base-date-range-picker
        v-if="range === 'custom'"
        class="booking-mobile-toolbar__custom"
        size="sm"
        :value="customRange"
        :max-days="MAX_RANGE_DAYS"
        @input="value => emit('custom-range', value)"
      />
      <base-date-navigator
        v-else
        class="booking-mobile-toolbar__dates"
        size="sm"
        :mode="navMode"
        :value="navValue"
        :format="navFormat"
        @change="value => emit('navigate', value)"
      />
    </div>

    <div class="booking-mobile-toolbar__controls" :class="{ 'booking-mobile-toolbar__controls--first': range === 'rolling-30' }">
      <base-range-selector
        class="booking-mobile-toolbar__range"
        :value="range"
        :options="rangeOptions"
        @input="value => emit('range', value)"
      />
      <button
        v-if="showFilterButton"
        type="button"
        class="booking-mobile-toolbar__filter"
        :class="{ 'is-open': filtersOpen }"
        :aria-expanded="filtersOpen ? 'true' : 'false'"
        aria-label="Filters"
        @click="emit('update:filtersOpen', !filtersOpen)"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M7 12h10M10 18h4" />
        </svg>
        <span v-if="filterCount > 0" class="booking-mobile-toolbar__badge">{{ filterCount }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { PropType } from 'vue'
import moment from 'moment'
import { MAX_RANGE_DAYS, computeRangeWindow } from '@/util/bookingRange'
import type { BookingRange } from '@/util/bookingRange'

const props = defineProps({
  range: { type: String as PropType<BookingRange>, required: true },
  rangeOptions: { type: Array as PropType<{ value: BookingRange; label: string }[]>, required: true },
  // The view's current date (YYYY-MM-DD)
  anchor: { type: String, required: true },
  firstDayOfWeek: { type: [Number, String], default: null },
  customRange: { type: Array as PropType<string[]>, default: () => [] },
  filterCount: { type: Number, default: 0 },
  filtersOpen: { type: Boolean, default: false },
  showFilterButton: { type: Boolean, default: true },
  // Views with their own phone breakpoint (Resource calendar: < 640px) show it themselves
  alwaysShow: { type: Boolean, default: false }
})

const emit = defineEmits<{
  (e: 'range', value: BookingRange): void
  (e: 'navigate', value: string): void
  (e: 'custom-range', value: string[]): void
  (e: 'update:filtersOpen', value: boolean): void
}>()

const rangeWindow = computed(() => computeRangeWindow(
  props.range, props.anchor, props.customRange, props.firstDayOfWeek as number | string | null
))

const navMode = computed(() => (props.range === 'week' || props.range === 'month' ? props.range : 'day'))

// Week shows its first day, so the label can read "Sep 27 – Oct 3"
const navValue = computed(() => (props.range === 'week' ? rangeWindow.value.startDate : props.anchor))

// Element picker format tokens; [..] is literal text (the week's end date)
const navFormat = computed(() => {
  if (props.range === 'month') return 'MMM yyyy'
  if (props.range === 'week') return 'MMM d [– ' + moment(rangeWindow.value.endDate, 'YYYY-MM-DD').format('MMM D') + ']'
  return 'MMM d'
})
</script>

<script lang="ts">
export default { name: 'BookingMobileToolbar' }
</script>

<style lang="scss" scoped>
.booking-mobile-toolbar {
  display: none;
}

@media (max-width: 991.98px) {
  .booking-mobile-toolbar {
    display: block;
  }
}

.booking-mobile-toolbar.booking-mobile-toolbar--always {
  display: block;
}

.booking-mobile-toolbar__top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 12px;

  ::v-deep .view-switcher { margin-right: 0 !important; margin-top: 0 !important; }
}

.booking-mobile-toolbar__gear {
  order: 10;
  margin-left: auto;

  ::v-deep > * { margin: 0 !important; }
}

.booking-mobile-toolbar__nav {
  display: flex;
  align-items: center;
  margin: 16px 0 8px;
}

.booking-mobile-toolbar__custom { flex: 1 1 100%; min-width: 0; }

/* Row 2: the date control in the same outlined 40px style as row 3, flush to both edges:
   [‹] [date] [›] [Today] with 8px gaps (5-class selectors outrank BaseDateNavigator's scoped
   compact styles) */
.booking-mobile-toolbar .booking-mobile-toolbar__nav .booking-mobile-toolbar__dates.base-date-navigator {
  flex: 1 1 100%;
  min-width: 0;
  gap: 8px;

  ::v-deep .date-navigator__btn,
  ::v-deep .date-navigator__today-btn {
    height: 40px;
    min-height: 40px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    background: transparent;
    color: rgba(255, 255, 255, 0.7);
  }

  ::v-deep .date-navigator__btn { width: 40px; }

  ::v-deep .date-navigator__today-btn {
    padding: 0 10px;
    font-size: 13px;
  }

  ::v-deep .el-input__inner {
    height: 40px;
    line-height: 40px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    background: transparent;
    /* The theme forces 30px on every date input (_plugin-datepicker.scss, !important) for the
       icon; this box has no icon, so it needs !important to take the space back */
    padding-left: 6px !important;
    padding-right: 6px;
    font-size: 12px;
    text-align: center;
  }

  /* No calendar icon inside the date box: at ~285px it leaves room for the full label */
  ::v-deep .el-input__prefix { display: none; }
}

/* Row 3: full-width range select and a square filter button, both 40px tall */
.booking-mobile-toolbar__controls {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

/* Rolling 30 days has no dates row: keep the same 16px under row 1 */
.booking-mobile-toolbar__controls--first {
  margin-top: 16px;
}

/* Square 40px outlined action for row 1 (views put their Create "+" here), same style as the
   filter button so it is not louder than the rest of the toolbar */
.booking-mobile-toolbar__top ::v-deep .booking-mobile-toolbar__square {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-height: 40px;
  margin: 0;
  padding: 0;
  gap: 0;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  background: transparent;
  box-shadow: none;
  color: #e14eca;
  cursor: pointer;

  svg { width: 18px; height: 18px; }
}

.booking-mobile-toolbar__controls .booking-mobile-toolbar__range {
  flex: 1;
  min-width: 0;

  ::v-deep .base-range-selector__btn {
    justify-content: space-between;
    width: 100%;
    height: 40px;
  }
}

.booking-mobile-toolbar__filter {
  position: relative;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;

  &.is-open {
    border-color: #e14eca;
    color: #e14eca;
  }
}

.booking-mobile-toolbar__badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #e14eca;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
}

.white-content {
  .booking-mobile-toolbar .booking-mobile-toolbar__nav .booking-mobile-toolbar__dates.base-date-navigator {
    ::v-deep .date-navigator__btn,
    ::v-deep .date-navigator__today-btn,
    ::v-deep .el-input__inner {
      border-color: rgba(0, 0, 0, 0.2);
      color: rgba(0, 0, 0, 0.6);
    }
  }

  .booking-mobile-toolbar__top ::v-deep .booking-mobile-toolbar__square {
    border-color: rgba(0, 0, 0, 0.2);
  }

  .booking-mobile-toolbar__filter {
    border-color: rgba(0, 0, 0, 0.2);
    color: rgba(0, 0, 0, 0.6);

    &.is-open {
      border-color: #e14eca;
      color: #e14eca;
    }
  }
}
</style>
